import { IOrder } from './../entities/order.entity';
import { apiDelete, apiGet, apiPost, apiPut } from "@app/utils/http";

import { IVendor } from '@app/entities/vendor.entity';
import { IProduct } from '@app/entities/product.entity';

export interface ICartTotal {
    subtotal: number;
    discountTotal: number;
    shippingTotal: number;
    total: number;
}

export interface ICartShippingOption {
    cost: number;
    methodId: string;
    methodInstanceId: number;
    name: string;
    disabled: boolean;
    carryout: boolean;
    minCart: number;
}

export interface ICartItem {
    id: number;
    wcId: number;
    name: string;
    altName: string;
    // price: string;
    quantity: number;
    subtotal: number;
    total: number;
    product: IProduct;
    shippingClass: string
}

export interface ICartNotices {
    error?: string[];
}

export interface ICartCoupon {
    coupon: string;
    savingFloat: number;
    type: string
}

export interface ICartVendor extends IVendor {
    /** Whether the vendor is selectable for delivery now (configured + in range + minimum met). */
    shippable: { [key: string]: boolean };
    /** Whether the admin has enabled delivery for this vendor (has_delivery + Glovo phone),
     *  independent of cart value/distance. Controls whether the option is shown (possibly disabled). */
    deliveryConfigured?: { [key: string]: boolean };
    /** Per-carrier minimum vendor subtotal required to enable delivery. */
    deliveryMinimum?: { [key: string]: number };
    shippableValue: number;
    shippingCost: { [key: string]: number };
    /** Distance in km from the vendor to the customer; null when no address entered yet. */
    distance?: number | null;
    estimatedTime: string;
}

export interface ICartResponse {
    items: ICartItem[];
    itemCount: number;
    coupons: ICartCoupon[];
    currency: string;
    totals: ICartTotal;
    needsShipping: boolean;
    needsPayment: boolean;
    notices: ICartNotices;
    vendors: ICartVendor[];
    shippingOptions: ICartShippingOption[];
    /** Max delivery distance in km (e.g. 10). Used for "delivery too far" messaging. */
    maxDeliveryDistanceKm?: number;
    /** Products removed by WC session validation (expired/unpublished) */
    expiredItems?: { wcId: number; name: string }[];

    couponAddedResult?: any;
    couponRemovedResult?: any;
}

export interface ICheckoutRequest {
    stripeToken?: string;
    shippingClasses?: string[];
    orderId: string | number;
    /** E2E only: ask the server for a card-only PaymentIntent matching the card-only Element. */
    e2e?: boolean;
    user: {
        name: string;
        mail: string;
        phone: string;
        city: string;
        zip: string;
        address: string;
        addressNote: string;
        password: string;
    };
}

export interface ICheckoutResponse {
    stripeClientSecret: string;
    orderId: number;
    user?: {
        jwt: string;
        id: number;
        name: string;
        firstName: string;
        lastName: string;
        mail: string;
    }
    errors?: { [key: string]: string[] };
}

export default class CartService {
    static get(): Promise<ICartResponse> {
        return apiGet(`@api/cart`, )
                .then(response => (response as any) as ICartResponse);
    }

    static customer(data: any): Promise<ICheckoutResponse> {
        return apiPost(`@api/cart/customer`, data)
                .then(response => (response as any) as ICheckoutResponse);
    }

    static intent(data: ICheckoutRequest): Promise<ICheckoutResponse> {
        return apiPost(`@api/cart/intent`, data)
                .then(response => (response as any) as ICheckoutResponse);
    }

    static failed(data: any): Promise<ICheckoutResponse> {
        return apiPost(`@api/cart/failed`, data)
                .then(response => (response as any) as ICheckoutResponse);
    }

    static complete(data: { order_id: string | number; payment_intent_id?: string }): Promise<ICheckoutResponse> {
        return apiPost(`@api/cart/complete`, data)
                .then(response => (response as any) as ICheckoutResponse);
    }

    static shipping(id: ICartShippingOption): Promise<ICheckoutResponse> {
        return apiPost('@api/cart/shipping', id)
            .then(response => (response as any) as ICheckoutResponse);
    }

    static addCartItem(id: number, quantity: number): Promise<ICheckoutResponse> {
        // Validate ID before making API call
        if (!id || typeof id !== 'number' || id <= 0) {
            console.error('Invalid product ID for addCartItem:', id);
            return Promise.reject(new Error(`Invalid product ID: ${id}`));
        }
        return apiPut(`@api/cart/item/${id}`, {quantity: quantity})
                .then(response => (response as any) as ICheckoutResponse);
    }

    static updateItem(id: number, quantity: number): Promise<Response> {
        // Validate ID before making API call
        if (!id || typeof id !== 'number' || id <= 0) {
            console.error('Invalid product ID for updateItem:', id);
            return Promise.reject(new Error(`Invalid product ID: ${id}`));
        }
        return apiPost(`@api/cart/item/${id}`, { quantity: quantity });
    }

    static removeItem(id: number): Promise<Response> {
        // Validate ID before making API call
        if (!id || typeof id !== 'number' || id <= 0) {
            console.error('Invalid product ID for removeItem:', id);
            return Promise.reject(new Error(`Invalid product ID: ${id}`));
        }
        return apiDelete(`@api/cart/item/${id}`);
    }

    static addCoupon(code: string): Promise<ICartResponse> {
        return apiPost(`@api/cart/coupon/${code}`, {})
                .then(response => (response as any) as ICartResponse);
    }

    static removeCoupon(code: string): Promise<ICartResponse> {
        return apiDelete(`@api/cart/coupon/${code}`)
                .then(response => (response as any) as ICartResponse);
    }

    static confirm(orderId: number): Promise<IOrder> {
        return apiGet(`@api/cart/confirm/${orderId}`)
                .then(order => (order as any) as IOrder);
    }
}